import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { StatistiquesFilters } from './useStatistiquesFilters';
import { format } from 'date-fns';

export interface IndividualCampaignData {
  id: string;
  candidate_name: string;
  target_position: string;
  status: string; // 'Terminé' ou 'Refusé'
  date: string; // YYYY-MM-DD
  date_time: string; // ISO string
  taux_participation: number | null; // 0-100%, null si pas de référents sollicités
  delai_jours: number; // Délai jusqu'à complétion ou refus
  is_refused: boolean; // true si candidat a refusé
}

export const useIndividualCampaigns = (filters: StatistiquesFilters) => {
  const [campaignsData, setCampaignsData] = useState<IndividualCampaignData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCampaignsData = useCallback(async () => {
    let isMounted = true;

    try {
      setLoading(true);

      // Récupérer l'utilisateur connecté
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Utilisateur non connecté');

      // Récupérer l'organization_id
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('id', user.id)
        .single() as { data: { organization_id: string } | null; error: any };

      if (profileError) throw profileError;
      if (!profile?.organization_id) {
        throw new Error('Organisation non trouvée');
      }

      // Transformer 'all' en null pour la fonction SQL
      const positionParam = filters.position === 'all' ? null : filters.position;

      // Déterminer quelle fonction SQL utiliser
      let data, rpcError;

      if (filters.customPeriod?.from && filters.customPeriod?.to) {
        // Utiliser la fonction avec dates exactes
        const fromDate = format(filters.customPeriod.from, 'yyyy-MM-dd');
        const toDate = format(filters.customPeriod.to, 'yyyy-MM-dd');

        const result = await (supabase as any)
          .rpc('get_individual_completed_campaigns_exact', {
            p_org_id: profile.organization_id,
            p_from_date: fromDate,
            p_to_date: toDate,
            p_position: positionParam
          });

        data = result.data;
        rpcError = result.error;
      } else {
        // Utiliser la fonction avec période prédéfinie
        const result = await (supabase as any)
          .rpc('get_individual_completed_campaigns', {
            p_org_id: profile.organization_id,
            p_period: filters.period || '7d',
            p_position: positionParam
          });

        data = result.data;
        rpcError = result.error;
      }

      if (rpcError) {
        console.error('❌ Erreur RPC individual campaigns:', rpcError);
        throw rpcError;
      }

      // Ne mettre à jour que si le composant est toujours monté
      if (!isMounted) return;

      // Parser les données
      let parsedData: IndividualCampaignData[] = [];

      if (Array.isArray(data)) {
        parsedData = data;
      } else if (data && typeof data === 'object') {
        const firstValue = Object.values(data)[0];
        if (Array.isArray(firstValue)) {
          parsedData = firstValue as IndividualCampaignData[];
        }
      }

      setCampaignsData(parsedData);
      setError(null);
    } catch (err) {
      if (isMounted) {
        console.error('Erreur lors du chargement des campagnes individuelles:', err);
        setError(err as Error);
      }
    } finally {
      if (isMounted) {
        setLoading(false);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [filters.period, filters.position, filters.customPeriod]);

  useEffect(() => {
    fetchCampaignsData();
  }, [fetchCampaignsData]);

  return { campaignsData, loading, error, refetch: fetchCampaignsData };
};
